import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "~/components/ui/use-toast";
import type { TPartImage, TPartInteractionWithImage } from "~/database/types";
import { useApiClient } from "~/hooks/use-api-client";
import { createPartDefaultImageUpdater } from "~/lib/cache-update-utils";

import type { SessionCacheData } from "../use-session"; // Assuming path based on user's previous change

// Type for the mutation input variables
interface GeneratePartImageVariables {
    partId: string;
    partInteractionId?: string | null;
    sessionId?: string; // Added sessionId
    style: TPartImage["style"]; // Use the enum type from DB if possible
    prompt?: string;
}

// Updated: Type for the expected API response, now includes the created image record

/**
 * Hook to trigger the generation of a new part image.
 * Returns a success boolean & newly created image record on success.
 */
export function useGeneratePartImage(
    // Add optional callback to receive the new image ID
    {
        onSuccessCallback,
        sessionToken,
        replaceExisting = false, // New parameter to control replacement behavior
        suppressToast = false, // New parameter to suppress individual toasts for batch operations
    }: {
        onSuccessCallback?: (newImageId: string) => void;
        sessionToken?: string | null;
        replaceExisting?: boolean; // When true, replaces existing default images
        suppressToast?: boolean; // When true, suppresses individual success/error toasts
    } = {},
) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation<
        { success: boolean; newImage: TPartImage },
        Error,
        GeneratePartImageVariables
    >({
        mutationFn: async ({ partId, style, prompt, partInteractionId }) => {
            // AIDEV-NOTE: partId now sent in request body
            const response = await apiClient.post(`/part/image`, {
                partId,
                style,
                prompt,
                partInteractionId,
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch (e) {
                    // Ignore
                }
                const errorMessage =
                    errorData?.error ||
                    `HTTP error! status: ${response.status}`;
                throw new Error(errorMessage);
            }

            return response.json() as Promise<{
                success: boolean;
                newImage: TPartImage;
            }>;
        },
        onSuccess: (newImageResponse, variables) => {
            const { partId, sessionId, partInteractionId } = variables;

            // Attempt to update the session cache
            // When replaceExisting=true, always update; otherwise only if no existing default image
            const condition = replaceExisting
                ? undefined // No condition - always update
                : (partInteraction: TPartInteractionWithImage) =>
                      !partInteraction.defaultImageId;
            const updater = createPartDefaultImageUpdater(
                partId,
                newImageResponse.newImage.id,
                newImageResponse.newImage,
                condition,
            );
            queryClient.setQueryData<SessionCacheData | undefined>(
                ["session", sessionId, sessionToken],
                updater,
            );

            // AIDEV-NOTE: Invalidate all partImages queries for this part (with and without partInteractionId)
            // This ensures both the general image list and interaction-specific lists are refreshed
            queryClient.invalidateQueries({
                queryKey: ["partImages", partId],
            });

            // Only show toast if not suppressed (for individual generations)
            if (!suppressToast) {
                toast({
                    title: "Image Generation Started",
                    description: "Your new image is being created.",
                });
            }

            // Execute the callback with the new image ID
            if (onSuccessCallback) {
                onSuccessCallback(newImageResponse.newImage.id);
            }
        },
        onError: (error) => {
            // Only show toast if not suppressed (for individual generations)
            if (!suppressToast) {
                toast({
                    title: "Generation Failed",
                    description:
                        error.message || "Could not start image generation.",
                    variant: "destructive",
                });
            }
        },
    });
}
